import {
  Crosshair,
  FolderOpen,
  Building2,
  XCircle,
  Archive,
  LayoutGrid,
  Home,
  Building,
  TreePine,
  Store,
  Blend,
  HelpCircle,
  type LucideIcon,
} from "lucide-react"
import type { ProjectStatus } from "@/types"

// --- Status ---

export const STATUS_CONFIG: Record<ProjectStatus, { label: string; icon: LucideIcon; color: string }> = {
  pending: { label: "Prospects", icon: Crosshair, color: "#3b82f6" },
  accepted: { label: "Dossiers", icon: FolderOpen, color: "#f59e0b" },
  asset: { label: "Patrimoine", icon: Building2, color: "#22c55e" },
  rejected: { label: "Rejetés", icon: XCircle, color: "#ef4444" },
  archived: { label: "Archivés", icon: Archive, color: "#6b7280" },
}

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  pending: "En attente",
  accepted: "Accepté",
  rejected: "Rejeté",
  archived: "Archivé",
  asset: "Actif",
}

export const STATUS_FILTERS = [
  { value: "", label: "Tous", icon: LayoutGrid },
  { value: "0", label: "Prospects", icon: Crosshair },
  { value: "1", label: "Dossiers", icon: FolderOpen },
  { value: "4", label: "Patrimoine", icon: Building2 },
  { value: "2", label: "Rejetés", icon: XCircle },
  { value: "3", label: "Archivés", icon: Archive },
] as const

export const STATUS_BASE_URLS: Record<ProjectStatus, string> = {
  pending: "/prospects",
  accepted: "/projects",
  asset: "/patrimoine",
  rejected: "/rejected",
  archived: "/archives",
}

export function projectShowPath(project: { id: string; status: ProjectStatus }, navKey?: string): string {
  const base = STATUS_BASE_URLS[project.status] || "/projects"
  const path = `${base}/${project.id}`
  return navKey ? `${path}?nav=${navKey}` : path
}

// --- Property type ---

export const PROPERTY_TYPE_CONFIG: Record<string, { label: string; icon: LucideIcon }> = {
  house: { label: "Maison", icon: Home },
  apartment: { label: "Appartement", icon: Building2 },
  land: { label: "Terrain", icon: TreePine },
  building: { label: "Immeuble", icon: Building },
  commercial: { label: "Commercial", icon: Store },
  mixed: { label: "Mixte", icon: Blend },
  other: { label: "Autre", icon: HelpCircle },
}

export const PROPERTY_TYPE_VALUES: Record<string, string> = {
  house: "0",
  apartment: "1",
  land: "2",
  building: "3",
  commercial: "4",
  mixed: "5",
  other: "6",
}

// --- Origin ---

export const ORIGIN_LABELS: Record<string, string> = {
  kaptcher: "Kaptcher",
  terrafinder: "Terrafinder",
  creation: "Création manuelle",
  other_origin: "Autre",
}

// --- Sort ---

export const SORT_OPTIONS = [
  { value: "name", label: "Nom" },
  { value: "city_name", label: "Ville" },
  { value: "zip_code", label: "Code postal" },
  { value: "lead_score", label: "Score" },
  { value: "pipe_status_id", label: "Suivi" },
  { value: "property_type", label: "Type de bien" },
  { value: "origin", label: "Origine" },
  { value: "field_surface", label: "Surface terrain" },
  { value: "house_surface", label: "Surface bâti" },
  { value: "created_at", label: "Date de création" },
  { value: "updated_at", label: "Dernière modification" },
] as const

// --- Columns ---

export type ColumnKey = "name" | "property_type" | "contact" | "pipe_status" | "dates" | "actions"

export const COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: "name", label: "Projet" },
  { key: "property_type", label: "Parcelle" },
  { key: "contact", label: "Contact" },
  { key: "pipe_status", label: "Suivi" },
  { key: "dates", label: "Dates" },
  { key: "actions", label: "" },
]

// --- Advanced filters ---

export const ADVANCED_FILTER_KEYS = [
  "property_type_eq",
  "created_by_id_eq",
  "assigned_teams_id_eq",
  "project_tags_id_in",
  "field_surface_gteq",
  "field_surface_lteq",
  "house_surface_gteq",
  "house_surface_lteq",
  "created_at_gteq",
  "created_at_lteq",
] as const

// --- Utilities ---

export function formatSurface(label: string, value: number | string | null): string | null {
  if (value == null) return null
  return `${label} ${Number(value).toLocaleString("fr-FR")} m²`
}

export function getScoreColor(score: number): string {
  if (score <= 3) return "#ef4444"
  if (score <= 6) return "#f59e0b"
  return "#22c55e"
}
